import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Alert,
  CircularProgress,
  Chip,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../theme/theme';
import {
  getUserCompany,
  getCompanyUsers,
  removeUserFromCompany,
  addEnterpriseAdmin,
  removeEnterpriseAdmin,
  findUserByEmail,
  inviteUserToCompany
} from '../../lib/services/enterpriseCompanyService';

/**
 * Enterprise Admin Panel
 * 
 * Comprehensive admin interface for Enterprise company management
 * with tabs for Dashboard, Users, and Admins
 */
const EnterpriseAdminPanel = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Invite dialog state
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  
  // User menu state
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyData = await getUserCompany(currentUser.uid);
      
      if (!companyData) {
        setError('No enterprise company found for this user');
        return;
      }
      
      setCompany(companyData);
      
      const companyUsers = await getCompanyUsers(companyData.id);
      setUsers(companyUsers);
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError('Failed to load enterprise company information');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInvite = async () => {
    if (!inviteEmail || !company) return;
    
    try {
      setInviting(true);
      setError(null);
      
      // Check if user exists
      const existingUser = await findUserByEmail(inviteEmail);
      
      if (existingUser) {
        // Check if user is already in a company
        if (existingUser.enterpriseCompanyId || existingUser.filmCompanyId) {
          setError(`User ${inviteEmail} is already part of another company.`);
          setInviting(false);
          return;
        }
        
        // Add existing user to company
        await inviteUserToCompany(company.id, existingUser.id, currentUser.uid);
        alert(`User ${inviteEmail} has been added to ${company.companyName}`);
      } else {
        // User doesn't exist - show message about invitation system
        alert(`User ${inviteEmail} not found. Invitation email system coming soon!\n\nFor now, please ask the user to sign up first, then invite them.`);
      }
      
      setInviteDialog(false);
      setInviteEmail('');
      fetchData();
    } catch (err) {
      console.error('Error inviting user:', err);
      setError(`Failed to invite user: ${err.message}`);
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (user) => {
    if (!window.confirm(`Remove ${user.email} from ${company.companyName}?`)) {
      return;
    }
    
    try {
      await removeUserFromCompany(company.id, user.id);
      alert('User removed successfully');
      fetchData();
    } catch (err) {
      console.error('Error removing user:', err);
      alert(`Failed to remove user: ${err.message}`);
    }
  };

  const handlePromoteToAdmin = async (user) => {
    if (!window.confirm(`Make ${user.email} an Enterprise Admin?`)) {
      return;
    }
    
    try {
      await addEnterpriseAdmin(company.id, user.id);
      alert(`${user.email} is now an Enterprise Admin`);
      fetchData();
    } catch (err) {
      console.error('Error promoting user:', err);
      alert(`Failed to promote user: ${err.message}`);
    }
  };

  const handleDemoteFromAdmin = async (user) => {
    if (!window.confirm(`Remove admin privileges from ${user.email}?`)) {
      return;
    }
    
    try {
      await removeEnterpriseAdmin(company.id, user.id);
      alert(`${user.email} is no longer an Enterprise Admin`);
      fetchData();
    } catch (err) {
      console.error('Error demoting user:', err);
      alert(`Failed to demote user: ${err.message}`);
    }
  };

  const handleUserMenuOpen = (event, user) => {
    setUserMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
    setSelectedUser(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSeatUtilization = () => {
    if (!company) return 0;
    return (company.usedSeats / company.totalSeats) * 100;
  };

  const canAddUsers = () => {
    return company && company.usedSeats < company.totalSeats;
  };

  const isUserAdmin = (user) => {
    return user.isFilmEnterpriseAdmin || user.isEnterpriseAdmin || 
           (company.enterpriseAdminUids && company.enterpriseAdminUids.includes(user.id));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !company) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Company not found'}
        </Alert>
      </Container>
    );
  }

  const admins = users.filter(u => isUserAdmin(u));
  const regularUsers = users.filter(u => !isUserAdmin(u));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: brandColors.primary }}>
          Enterprise Admin Panel
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {company.companyName}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Dashboard" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label={`Users (${users.length})`} icon={<PeopleIcon />} iconPosition="start" />
          <Tab label={`Admins (${admins.length})`} icon={<AdminIcon />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Dashboard Tab */}
      {activeTab === 0 && (
        <>
          {/* Company Info Card */}
          <Card elevation={3} sx={{ mb: 4, border: `2px solid ${brandColors.primary}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <BusinessIcon sx={{ fontSize: 48, color: brandColors.primary }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {company.companyName}
                  </Typography>
                  <Chip
                    label={company.subscriptionStatus || 'Active'}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Subscription Expires
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(company.subscriptionExpiresAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Active Users
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {users.length} users
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Admins
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {admins.length} admin(s)
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Seat Usage */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Seat Utilization
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={getSeatUtilization()}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      bgcolor: `${brandColors.primary}20`,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getSeatUtilization() > 90 ? '#f44336' : 
                                getSeatUtilization() > 75 ? '#ff9800' : brandColors.primary
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right', fontWeight: 600 }}>
                  {company.usedSeats} / {company.totalSeats} seats
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {(getSeatUtilization()).toFixed(1)}% utilized
              </Typography>
              
              {getSeatUtilization() > 90 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  You're running low on seats. Contact support to add more capacity.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(1)}
                  sx={{ bgcolor: brandColors.primary }}
                >
                  Manage Users
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(2)}
                  sx={{ bgcolor: brandColors.primary }}
                >
                  Manage Admins
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchData}
                >
                  Refresh Data
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Users Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Company Users ({users.length} of {company.totalSeats})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  label={`${company.totalSeats - company.usedSeats} seats available`}
                  color={canAddUsers() ? 'success' : 'error'}
                  size="small"
                />
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setInviteDialog(true)}
                  disabled={!canAddUsers()}
                  sx={{ bgcolor: brandColors.primary }}
                >
                  Invite User
                </Button>
              </Box>
            </Box>

            {!canAddUsers() && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                All seats are currently in use. Contact support to add more seats.
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Added</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {user.displayName || 'No Name'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isUserAdmin(user) ? (
                          <Chip
                            icon={<AdminIcon />}
                            label="Admin"
                            color="primary"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="User"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(user.addedToFilmCompanyAt || user.addedToEnterpriseCompanyAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(user.lastLogin)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleUserMenuOpen(e, user)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {users.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No users yet
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setInviteDialog(true)}
                  sx={{ mt: 2 }}
                >
                  Invite First User
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admins Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Enterprise Admins ({admins.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enterprise Admins can manage users and company settings. You can promote regular users to admins or demote admins to regular users.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Admin</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Added as Admin</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AdminIcon color="primary" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {admin.displayName || 'No Name'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {admin.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(admin.addedToFilmCompanyAt || admin.addedToEnterpriseCompanyAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {admin.id !== currentUser.uid && (
                          <Tooltip title="Remove Admin Privileges">
                            <IconButton
                              size="small"
                              onClick={() => handleDemoteFromAdmin(admin)}
                              color="warning"
                            >
                              <StarBorderIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {admin.id === currentUser.uid && (
                          <Chip label="You" size="small" color="primary" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {admins.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No admins found
                </Typography>
              </Box>
            )}

            {/* Regular Users Section */}
            {regularUsers.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Promote Users to Admin ({regularUsers.length} available)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {regularUsers.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {user.displayName || 'No Name'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.email}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Promote to Admin">
                              <IconButton
                                size="small"
                                onClick={() => handlePromoteToAdmin(user)}
                                color="primary"
                              >
                                <StarIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User to Company</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              helperText="Enter the email address of the user you want to invite"
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Available seats: {company.totalSeats - company.usedSeats} of {company.totalSeats}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleInvite} 
            variant="contained"
            disabled={!inviteEmail || inviting || !canAddUsers()}
            sx={{ bgcolor: brandColors.primary }}
          >
            {inviting ? <CircularProgress size={20} /> : 'Invite User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      >
        {selectedUser && !isUserAdmin(selectedUser) && (
          <MenuItem onClick={() => {
            handlePromoteToAdmin(selectedUser);
            handleUserMenuClose();
          }}>
            <AdminIcon sx={{ mr: 1 }} /> Promote to Admin
          </MenuItem>
        )}
        {selectedUser && isUserAdmin(selectedUser) && selectedUser.id !== currentUser.uid && (
          <MenuItem onClick={() => {
            handleDemoteFromAdmin(selectedUser);
            handleUserMenuClose();
          }}>
            <StarBorderIcon sx={{ mr: 1 }} /> Remove Admin
          </MenuItem>
        )}
        {selectedUser && !isUserAdmin(selectedUser) && (
          <MenuItem onClick={() => {
            handleRemove(selectedUser);
            handleUserMenuClose();
          }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} /> Remove from Company
          </MenuItem>
        )}
      </Menu>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          To add more seats, change billing information, or upgrade your plan, please contact support at sales@greenlightanalytics.com
        </Typography>
      </Alert>
    </Container>
  );
};

export default EnterpriseAdminPanel;

